import { useState, useEffect } from "react";

/**
 * Custom React hook to fetch data from a given URL with specified request options.
 * It manages loading, error handling, and storing the fetched data.
 * @component
 * @memberof Hooks
 * @param {string} url - The API endpoint to fetch data from.
 * @param {RequestInit} [requestOptions] - Optional fetch request options (e.g., headers, method, body).
 * @returns {{ data: any, loading: boolean, error: string | null }} - An object containing:
 * - `data`: The fetched response data (or `null` if not available).
 * - `loading`: A boolean indicating if the request is in progress.
 * - `error`: A string message if an error occurs, otherwise `null`.
 */

function useFetchData(url, requestOptions) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    /**
     * Fetches data from the provided URL.
     * Handles API response, updates state variables accordingly.
     */
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(url, requestOptions);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [url]); // Dependency array ensures the effect runs when `url` changes.

  return { data, loading, error };
}

export default useFetchData;
